import { observer } from 'mobx-react-lite';
import { isConnectedAccount } from 'owa-accounts-store';
import {
    categoryMenuSearchBoxPlaceholderText,
    categoryMenuNewCategoryTooltipText,
    categoryMenuNewCategoryText,
    categoryMenuAllCategoriesTooltip,
    categoryMenuAllCategoriesText,
    categoryMenuClearCategoriesText,
    categoryMenuClearCategoriesTooltipText,
    categoryMenuManageCategoriesTooltipText,
    categoryMenuManageCategoriesText,
} from './CategoryMenu.locstring.json';
import { newCategoryTitle } from './CategoryDialog.locstring.json';
import loc from 'owa-localize';
import { MailboxInfo } from 'owa-client-ids';
import NewCategoryMenuItem from './NewCategoryMenuItem';
import CategoryDialog from './CategoryDialog';
import resetNewCategoryViewState from '../actions/newCategoryMenu/resetNewCategoryViewState';
import setInitialNewCategoryViewState from '../actions/newCategoryMenu/setInitialNewCategoryViewState';
import setCategoryMenuFindText from '../actions/setCategoryMenuFindText';
import setIsFocusInSearchBox from '../actions/setIsFocusInSearchBox';
import setShouldShowAllCategories from '../actions/setShouldShowAllCategories';
import addCategory from '../actions/addCategory';
import categoryStore from '../store/store';
import { DEFAULT_CATEGORY_COLOR_CODE } from '../utils/categoriesConstants';
import CategoryActionSource from '../utils/CategoryActionSource';
import getCategorySuggestions from '../utils/getCategorySuggestions';
import { isCategoryNameValid } from '../utils/getCategoryNameErrorText';
import {
    FocusZone,
    FocusZoneDirection,
    FocusZoneTabbableElements,
} from 'office-ui-fabric-react/lib/FocusZone';
import { DirectionalHint } from 'office-ui-fabric-react/lib/Tooltip';
import { logUsage } from 'owa-analytics';
import CategoryType from 'owa-service/lib/contract/CategoryType';

import * as React from 'react';
import {
    SearchBox,
    ISearchBoxStyleProps,
    ISearchBoxStyles,
    ISearchBox,
} from 'office-ui-fabric-react/lib/SearchBox';
import { CategoryMenuItem } from './CategoryMenuItem';
import { isFeatureEnabled } from 'owa-feature-flags';

const styles = require('./CategoryMenu.scss');
const classNames = require('classnames/bind').bind(styles);
const MAX_CATEGORIES_WHEN_NOT_ALL_SHOWN = 8;

export interface CategoryMenuProps {
    actionSource: CategoryActionSource;
    categoryList: CategoryType[];
    categoriesToCheck: string[]; // Categories which are shown checked
    dismissMenu: () => void;
    shouldShowSearchBox: boolean;
    shouldShowManageCategories: boolean;
    shouldShowClearCategories: boolean;
    addFavoriteCategory: (categoryId: string, actionSource: CategoryActionSource) => void;
    onAfterCreateNewCategory: (category: string, addCategoryPromise?: Promise<boolean>) => void; // Called optimistically when user creates a new categories
    onCheckedCategoryClicked: (category: string) => void;
    onUncheckedCategoryClicked: (category: string) => void;
    manageCategoriesDisabled?: boolean;
    createNewCategoryDisabled?: boolean;
    onManageCategoryClicked?: () => void;
    onClearCategoriesClicked?: () => void;
    mailboxInfo?: MailboxInfo;
    directionalHint?: DirectionalHint;
    useTargetWidth?: boolean;
    coverTarget?: boolean;
}

export const CategoryMenu = observer(function CategoryMenu(props_0: CategoryMenuProps) {
    React.useEffect(() => {
        startTime.current = Date.now();
        logUsage('Category_Menu_Opened', [props_0.actionSource]);
        // Focus on search box after component mounts
        focusOnSearchBox();
    }, []);
    const searchBoxRef = React.useRef<ISearchBox>();
    const startTime = React.useRef<number>();
    /**
     * Stores the callback to be called when user hits enter in the search box
     */
    const onSearchBoxEnterCallback = React.useRef<() => void>();
    const shouldInputLoseFocusOnArrowKeyCallback = (inputElement: HTMLInputElement) => {
        // We want the up and down arrow keys on search input element to shift the focus to next and previous element respectively
        // irrespective of the default behavior where FocusZone does not permit it if shifting focus is only doable using Tab keys
        return true;
    };
    const dismissMenu = () => {
        props_0.dismissMenu();
    };
    const onSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>, findText: string) => {
        setCategoryMenuFindText(findText);
        setShouldShowAllCategories(false /* shouldShow */);
        resetNewCategoryViewState();
    };
    const onSearchBoxRender = () => {
        return (
            <SearchBox
                key={'searchCategoryMenu'}
                className={styles.categoryMenuItemContainerCommon}
                componentRef={searchBoxRef}
                placeholder={loc(categoryMenuSearchBoxPlaceholderText)}
                ariaLabel={loc(categoryMenuSearchBoxPlaceholderText)}
                onEscape={onEscapeOnSearchBox}
                onSearch={onEnterOnSearchBox}
                onChange={onSearchInputChange}
                onBlur={onBlurOnSearchBox}
                onFocus={onFocusOnSearchBox}
                styles={getSearchBoxStyles}
                value={categoryStore.categoryMenuViewState.findText}
            />
        );
    };
    const onFocusOnSearchBox = (evt: React.FocusEvent<EventTarget>) => {
        setIsFocusInSearchBox(true);
    };
    const onBlurOnSearchBox = (evt: React.FocusEvent<EventTarget>) => {
        setIsFocusInSearchBox(false);
        onSearchBoxEnterCallback.current = null;
    };
    const onEscapeOnSearchBox = (evt: React.MouseEvent<unknown>) => {
        // Dismiss the menu when user escapes while focus was on search box.
        // This needs to be handled separately than other menu items
        dismissMenu();
    };
    const onEnterOnSearchBox = (evt: React.MouseEvent<unknown>) => {
        if (onSearchBoxEnterCallback.current) {
            onSearchBoxEnterCallback.current();
        }
        logUsage('Category_Menu_SearchBoxEnterClicked', [props_0.actionSource]);
    };
    const focusOnSearchBox = () => {
        window.requestAnimationFrame(() => {
            searchBoxRef.current?.focus?.();
        });
    };
    const onCategoryClicked = (categoryName: string, isChecked: boolean, isFilterMode: boolean) => {
        // Dismiss menu first and then call the click handler
        dismissMenu();
        if (isChecked) {
            props_0.onCheckedCategoryClicked(categoryName);
        } else {
            props_0.onUncheckedCategoryClicked(categoryName);
        }
        const timeToPickCategoryInMillis = Date.now() - startTime.current;
        logUsage('Category_Menu_CategoryClicked', [
            isFilterMode,
            props_0.actionSource,
            timeToPickCategoryInMillis,
        ]);
    };
    const onNewCategoryClicked = (evt: React.MouseEvent<unknown>) => {
        evt.stopPropagation();
        setCategoryMenuFindText('');
        setInitialNewCategoryViewState();
        logUsage('Category_Menu_NewCategoryClicked', [props_0.actionSource]);
    };
    const onViewAllClicked = (evt: React.MouseEvent<unknown>) => {
        // Required so as the menu does not dismiss when view all is clicked.
        // VSO: 22703 - Investigate why preventDefault is required when view all item is clicked in the move to context menu
        evt.stopPropagation();
        evt.preventDefault();
        // Reset Search findText and new category menu view state
        setCategoryMenuFindText('');
        resetNewCategoryViewState();
        // Show All categories
        setShouldShowAllCategories(true /* shouldShow */);
        logUsage('Category_Menu_ViewAllClicked', [props_0.actionSource]);
        focusOnSearchBox();
    };
    const onManageCategoryClicked = (evt: React.MouseEvent<unknown>) => {
        dismissMenu();
        props_0.onManageCategoryClicked();
        logUsage('Category_Menu_ManageClicked', [props_0.actionSource]);
    };
    const onClearCategoriesClicked = (evt: React.MouseEvent<unknown>) => {
        dismissMenu();
        props_0.onClearCategoriesClicked();
        logUsage('Category_Menu_ClearClicked', [props_0.actionSource]);
    };
    /**
     * Determines whether to highlight the top item in the menu or not
     */
    const shouldHighlightTopItem = (): boolean => {
        // Top item can be highlighted only when
        // 1. Focus is in search box and
        // 2. There is text is the search box
        return (
            categoryStore.categoryMenuViewState.isFocusInSearchBox &&
            categoryStore.categoryMenuViewState.findText.length > 0
        );
    };
    const renderCategoryMenu = (): JSX.Element[] => {
        let categoriesToShow: string[] = [];
        let shouldShowCreateNewCategoryItem = false;
        let shouldShowAllCategoriesItem = false;
        let { findText, shouldShowAllCategories } = categoryStore.categoryMenuViewState;
        const isFilterMode = findText.trim().length > 0;
        // reset the onSearchBoxEnter call back
        onSearchBoxEnterCallback.current = null;
        // Filtered mode
        if (isFilterMode) {
            let filteredCategorySuggestions = getCategorySuggestions(
                findText,
                props_0.categoryList
            );
            filteredCategorySuggestions.suggestions.map(suggestion => {
                categoriesToShow.push(suggestion.Name);
            });
            shouldShowCreateNewCategoryItem = !filteredCategorySuggestions.hasExactMatch;
        } else {
            categoriesToShow = getCategoriesToShow(shouldShowAllCategories);
        }
        // Show one less category when not showing 'All Categories' and clear categories menu item is showing
        if (!shouldShowAllCategories) {
            const maxCategoriesToShow = props_0.shouldShowClearCategories
                ? MAX_CATEGORIES_WHEN_NOT_ALL_SHOWN - 1
                : MAX_CATEGORIES_WHEN_NOT_ALL_SHOWN;
            categoriesToShow.splice(maxCategoriesToShow);
            // Sort remaining mru filtered categories alphabetically, ignoring checked categories
            if (isFeatureEnabled('tri-categorySortAlphabetical')) {
                const sortedRemainingCategories = categoriesToShow
                    .slice(props_0.categoriesToCheck.length)
                    .sort((a, b) => {
                        return a.localeCompare(b);
                    });
                categoriesToShow = categoriesToShow
                    .slice(0, props_0.categoriesToCheck.length)
                    .concat(sortedRemainingCategories);
            }
            shouldShowAllCategoriesItem = categoriesToShow.length < props_0.categoryList.length;
        }
        let items: JSX.Element[] = [];
        // Add search box
        if (props_0.shouldShowSearchBox) {
            addSearchBox(items);
        }
        // Add categories
        const isTopItemHighlighted = addCategories(
            items,
            categoriesToShow,
            isFilterMode,
            findText,
            props_0.categoryList
        );
        // Add divider if we have more than 1 menu items, 1 is for search box and it has a border
        if (items.length > 1) {
            addDividerMenu(items, 'categoryMenu_extendedMenu_divider');
        }
        // Add "Create New" or "New Category" menu
        shouldShowCreateNewCategoryItem =
            shouldShowCreateNewCategoryItem &&
            !props_0.createNewCategoryDisabled &&
            isCategoryNameValid(findText);
        if (shouldShowCreateNewCategoryItem) {
            addCreateNewMenu(items, isTopItemHighlighted);
            addDividerMenu(items, 'categoryMenu_createNewCategory_divider');
        } else {
            if (
                !props_0.mailboxInfo ||
                !isConnectedAccount(props_0.mailboxInfo.primarySmtpAddress)
            ) {
                addNewCategoryMenu(items);
                addDividerMenu(items, 'categoryMenu_newCategory_divider');
            }
        }
        // Add "All categories" menu
        if (shouldShowAllCategoriesItem) {
            addViewAllMenu(items);
        }
        // Add "Clear categories" menu
        if (props_0.shouldShowClearCategories) {
            addClearCategoryMenu(items);
        }
        // Add "Manage categories" menu
        if (props_0.shouldShowManageCategories) {
            addManageCategoryMenu(items);
        }
        return items;
    };
    const getCategoriesToShow = (isAllMode: boolean): string[] => {
        const allCategories = [...props_0.categoryList];
        const doCategoriesHaveTimestamp = allCategories.every((category: CategoryType) => {
            return 'LastTimeUsed' in category;
        });
        let sortedCategories;
        // Sort categories by alphabetical order in all mode
        if (isAllMode) {
            sortedCategories = allCategories.sort((a, b) => {
                return a.Name.localeCompare(b.Name);
            });
            // Sort categories by most recently used
        } else if (
            doCategoriesHaveTimestamp &&
            (isFeatureEnabled('tri-categorySortMRU') ||
                isFeatureEnabled('tri-categorySortAlphabetical'))
        ) {
            sortedCategories = allCategories.sort((a, b) => {
                return b.LastTimeUsed.localeCompare(a.LastTimeUsed);
            });
        } else {
            // Sort categories by creation date
            sortedCategories = allCategories.reverse();
        }
        // Get just the category names
        let categoriesToShow = sortedCategories.map(category => category.Name);
        // Put checked categories at the top of the list if not showing 'All Categories'
        if (!isAllMode) {
            props_0.categoriesToCheck.forEach(category => {
                let index = categoriesToShow.indexOf(category);
                if (index != -1) {
                    categoriesToShow.splice(index, 1);
                }
                categoriesToShow.unshift(category);
            });
        }
        return categoriesToShow;
    };
    const addSearchBox = (items: JSX.Element[]) => {
        items.push(onSearchBoxRender());
    };
    const addCategories = (
        items: JSX.Element[],
        categoriesToShow: string[],
        isFilterMode: boolean,
        findText: string,
        categoryList: CategoryType[]
    ): boolean => {
        let isTopItemHighlighted = false;
        for (let i = 0; i < categoriesToShow.length; i++) {
            const categoryName = categoriesToShow[i];
            const isChecked = props_0.categoriesToCheck.indexOf(categoryName) > -1;
            let containerClass;
            // Highlight top item and store the callback
            if (i == 0 && shouldHighlightTopItem()) {
                containerClass = classNames(styles.highlightItem, styles.categoryMenuItemContainer);
                onSearchBoxEnterCallback.current = () => {
                    onCategoryClicked(categoryName, isChecked, isFilterMode);
                };
                isTopItemHighlighted = true;
            } else {
                containerClass = styles.categoryMenuItemContainer;
            }
            items.push(
                <CategoryMenuItem
                    key={'categoryMenu_' + categoryName}
                    categoryName={categoryName}
                    isChecked={isChecked}
                    className={containerClass}
                    isFilterMode={isFilterMode}
                    categoryList={categoryList}
                    findText={findText}
                    onCategoryClickedCallback={onCategoryClicked}
                    actionSource={props_0.actionSource}
                />
            );
        }
        return isTopItemHighlighted;
    };
    const addCreateNewMenu = (items: JSX.Element[], isTopItemHighlighted: boolean) => {
        let containerClass;
        // Highlight create new if top item is not highlighted
        if (!isTopItemHighlighted && shouldHighlightTopItem()) {
            containerClass = styles.highlightItem;
            onSearchBoxEnterCallback.current = () => {
                createNewCategoryOnEnterOnSearch();
            };
        }
        if (isFeatureEnabled('tri-newCategoryCreation')) {
            addCategoryDialogHostMenuItem(items, 'SearchCategoryCreateNew' /* actionSource */);
        }
        items.push(
            <NewCategoryMenuItem
                key={'createNewCategory'}
                actionSource={'SearchCategoryCreateNew'}
                containerClass={containerClass}
                isEditMode={false}
                createNew={createNewAndClickCategory}
            />
        );
    };
    const createNewCategoryOnEnterOnSearch = () => {
        createNewAndClickCategory(
            categoryStore.categoryMenuViewState.findText,
            DEFAULT_CATEGORY_COLOR_CODE,
            false /* shouldFavorite */,
            'SearchBoxMenu'
        );
    };
    const addNewCategoryMenu = (items: JSX.Element[]) => {
        if (categoryStore.categoryMenuViewState.newCategoryViewState) {
            if (isFeatureEnabled('tri-newCategoryCreation')) {
                addCategoryDialogHostMenuItem(items, 'NewCategoryButton' /* actionSource */);
            } else {
                items.push(
                    <NewCategoryMenuItem
                        key={'newCategory_Edit'}
                        actionSource={'NewCategoryButton'}
                        isEditMode={true}
                        createNew={createNewAndClickCategory}
                    />
                );
            }
        } else {
            let createNewCategoryClass = classNames(
                styles.extendedMenuItems,
                props_0.createNewCategoryDisabled && styles.disabled
            );
            let createNewCategoryOnClick = props_0.createNewCategoryDisabled
                ? undefined
                : onNewCategoryClicked;
            items.push(
                <div
                    key={'newCategory'}
                    className={styles.categoryMenuItemContainer}
                    data-is-focusable={true}
                    role={'menuitem'}
                    onClick={createNewCategoryOnClick}>
                    <div
                        title={loc(categoryMenuNewCategoryTooltipText)}
                        className={createNewCategoryClass}>
                        {loc(categoryMenuNewCategoryText)}
                    </div>
                </div>
            );
        }
    };
    const addCategoryDialogHostMenuItem = (
        items: JSX.Element[],
        actionSource: CategoryActionSource
    ) => {
        items.push(
            <CategoryDialog
                key={'categoryDialog'}
                actionSource={actionSource}
                createNew={createNewAndClickCategory}
                title={loc(newCategoryTitle)}
            />
        );
    };
    const addViewAllMenu = (items: JSX.Element[]) => {
        items.push(
            <div
                key={'categoriesViewAll'}
                className={styles.categoryMenuItemContainer}
                data-is-focusable={true}
                role={'menuitem'}
                onClick={onViewAllClicked}>
                <div
                    title={loc(categoryMenuAllCategoriesTooltip)}
                    className={styles.extendedMenuItems}>
                    {loc(categoryMenuAllCategoriesText)}
                </div>
            </div>
        );
    };
    const createNewAndClickCategory = (
        name: string,
        colorId: number,
        shouldFavorite: boolean,
        menuItemActionSource: CategoryActionSource
    ) => {
        dismissMenu();
        addCategory(
            name,
            colorId,
            shouldFavorite,
            menuItemActionSource,
            props_0.addFavoriteCategory,
            props_0.mailboxInfo,
            props_0.onAfterCreateNewCategory
        );
    };
    const addClearCategoryMenu = (items: JSX.Element[]) => {
        items.push(
            <div
                key={'clearCategories'}
                className={styles.categoryMenuItemContainer}
                data-is-focusable={true}
                role={'menuitem'}
                onClick={onClearCategoriesClicked}>
                <div
                    title={loc(categoryMenuClearCategoriesText)}
                    className={styles.extendedMenuItems}>
                    {loc(categoryMenuClearCategoriesTooltipText)}
                </div>
            </div>
        );
    };
    const addManageCategoryMenu = (items: JSX.Element[]) => {
        let manageCategoryClass = classNames(
            styles.extendedMenuItems,
            props_0.manageCategoriesDisabled && styles.disabled
        );
        let manageCategoryOnClick = props_0.manageCategoriesDisabled
            ? undefined
            : onManageCategoryClicked;
        items.push(
            <div
                key={'categoriesEdit'}
                className={styles.categoryMenuItemContainer}
                data-is-focusable={true}
                role={'menuitem'}
                onClick={manageCategoryOnClick}>
                <div
                    title={loc(categoryMenuManageCategoriesTooltipText)}
                    className={manageCategoryClass}>
                    {loc(categoryMenuManageCategoriesText)}
                </div>
            </div>
        );
    };
    const addDividerMenu = (items: JSX.Element[], key: string) => {
        items.push(<div className={styles.categoryMenuDivider} />);
    };
    // Gets the custom styles for the search box to
    // disable animation and hiding of the search icon
    // setting the search icon color to neutral primary
    const getSearchBoxStyles = (props: ISearchBoxStyleProps): Partial<ISearchBoxStyles> => {
        const { palette } = props.theme;
        return {
            root: [
                {
                    paddingLeft: 0,
                },
            ],
            icon: [
                {
                    opacity: 1,
                    transition: 'none',
                },
            ],
            iconContainer: [
                {
                    width: 30,
                    color: palette.neutralPrimary,
                },
            ],
        };
    };
    return (
        <FocusZone
            direction={FocusZoneDirection.bidirectional}
            isCircularNavigation={true}
            handleTabKey={FocusZoneTabbableElements.all} // The menu is Tab accessible
            shouldInputLoseFocusOnArrowKey={shouldInputLoseFocusOnArrowKeyCallback}>
            {renderCategoryMenu()}
        </FocusZone>
    );
});
